import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "../../icons/svg/outline";
import {
  resolveSortValue,
  resolveValue,
  rowMatchesSearch,
} from "../../../utils/collections/columnValues";
import { compareSortValues } from "../../../utils/collections/sort";
import { TooltipArrow } from "../tooltips";

/**
 * Renders a sortable/static header cell.
 *
 * @param {Object} props - Header cell props.
 * @param {Object} props.column - Column config.
 * @param {boolean} props.isActive - Whether this column is the active sort field.
 * @param {"asc"|"desc"} props.sortDirection - Current sort direction.
 * @param {Function} props.onSort - Sort click handler.
 * @returns {JSX.Element} Header cell.
 */
function HeaderCell({ column, isActive, sortDirection, onSort }) {
  const sortable = column.sortable !== false;
  const align = column.align === "right" ? "text-right" : "text-left";
  const baseHeaderClass =
    column.headerClassName ||
    `px-2 py-2 ${align} font-semibold`;
  const interactiveClass = sortable
    ? "cursor-pointer select-none hover:bg-gray-200/60"
    : "";
  // Prefer custom header tip; otherwise show full label when truncated.
  const headerTitle = column.headerTitle || column.label;

  return (
    <th
      className={`overflow-hidden ${baseHeaderClass} ${interactiveClass}`}
      style={column.width ? { width: column.width } : undefined}
      title={headerTitle}
      onClick={sortable ? () => onSort(column.key) : undefined}
    >
      <div
        className={`flex min-w-0 max-w-full items-center gap-0.5 ${
          column.align === "right" ? "justify-end" : "justify-start"
        }`}
      >
        <span className="min-w-0 truncate">{column.label}</span>
        {sortable ? (
          isActive && sortDirection === "asc" ? (
            <ChevronUp className="h-3 w-3 flex-shrink-0 text-gray-800" />
          ) : (
            <ChevronDown
              className={`h-3 w-3 flex-shrink-0 ${
                isActive ? "text-gray-800" : "text-gray-300"
              }`}
            />
          )
        ) : null}
      </div>
    </th>
  );
}

/**
 * Renders a single body cell with optional click, native title, or hover popup.
 *
 * @param {Object} props - Cell props.
 * @param {Object} props.row - Data row.
 * @param {Object} props.column - Column config.
 * @param {string} props.cellClassName - Default cell className.
 * @returns {JSX.Element} Table cell.
 */
function BodyCell({ row, column, cellClassName }) {
  const align = column.align === "right" ? "text-right" : "text-left";
  const resolvedClassName =
    column.className || `${cellClassName} ${align}`;
  const value = resolveValue(row, column);
  const content =
    typeof column.render === "function" ? column.render(row) : value;
  const title =
    typeof column.title === "function" ? column.title(row) : column.title;
  const hoverContent =
    typeof column.hoverContent === "function"
      ? column.hoverContent(row)
      : column.hoverContent;
  const hasCellClick = typeof column.onClick === "function";

  /**
   * Stops row-level bubbling and runs the column `onClick` for this cell.
   *
   * @param {React.MouseEvent} event Cell click event.
   * @returns {void}
   */
  const handleClick = (event) => {
    if (!hasCellClick) return;
    event.stopPropagation();
    column.onClick(row, event);
  };

  let inner = content;
  if (hoverContent) {
    inner = (
      <span className="relative inline-flex">
        {content}
        <TooltipArrow
          text={hoverContent}
          position="below"
          align={column.align === "right" ? "end" : "center"}
          usePortal
          closeDelay={0}
          className={
            column.hoverClassName ||
            "!pointer-events-none !whitespace-normal !bg-white !text-left shadow-md shadow-gray-400/40"
          }
        />
      </span>
    );
  } else if (title) {
    inner = <span title={title}>{content}</span>;
  }

  return (
    <td
      className={`${resolvedClassName} ${hasCellClick ? "cursor-pointer" : ""}`}
      onClick={hasCellClick ? handleClick : undefined}
    >
      {inner}
    </td>
  );
}

/**
 * SimpleTable - Reusable columnar table with sort, optional search, and rich cells.
 *
 * Default styling matches the payout timesheets compact table. Pass Tailwind
 * className overrides for wrapper, table, header, body, rows, and cells.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.columns - Column configs (key, label, render, etc.).
 * @param {Array<Object>} props.data - Row data objects.
 * @param {Function} [props.getRowKey] - Stable row key resolver.
 * @param {Function} [props.getRowProps] - Extra props for each `<tr>` (className, onClick, ...).
 * @param {{ key: string, direction?: "asc"|"desc" }|null} [props.defaultSort=null] - Initial sort field/direction.
 * @param {boolean} [props.includeSearch=false] - Show live filter search above the table.
 * @param {string} [props.searchPlaceholder="Search..."] - Search input placeholder.
 * @param {string} [props.emptyMessage="No results."] - Empty-state message.
 * @param {string} [props.className] - Outer wrapper classes.
 * @param {string} [props.tableClassName] - `<table>` classes.
 * @param {string} [props.theadClassName] - `<thead>` classes.
 * @param {string} [props.headerRowClassName] - Header `<tr>` classes.
 * @param {string} [props.tbodyClassName] - `<tbody>` classes.
 * @param {string} [props.rowClassName] - Default body row classes.
 * @param {string} [props.cellClassName] - Default body cell classes.
 * @param {string} [props.searchClassName] - Search input classes.
 * @returns {JSX.Element} Rendered table.
 */
const SimpleTable = ({
  columns = [],
  data = [],
  getRowKey = (row, index) => row?.id ?? index,
  getRowProps,
  defaultSort = null,
  includeSearch = false,
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
  className = "w-full min-w-0",
  tableClassName = "w-full table-fixed divide-y divide-gray-100",
  theadClassName = "bg-gray-100",
  headerRowClassName = "whitespace-nowrap text-[11px] uppercase tracking-wide text-gray-500",
  tbodyClassName = "divide-y divide-gray-100 bg-white",
  rowClassName = "group text-xs text-gray-700 transition hover:bg-primary-50/60",
  cellClassName = "px-2 py-1",
  searchClassName = "block w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-3 text-sm caret-black placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500",
}) => {
  const [sortField, setSortField] = useState(defaultSort?.key ?? null);
  const [sortDirection, setSortDirection] = useState(
    defaultSort?.direction === "desc" ? "desc" : "asc"
  );
  const [searchQuery, setSearchQuery] = useState("");

  /**
   * Toggles sort field/direction when a sortable header is clicked.
   *
   * @param {string} field - Column key to sort by.
   * @returns {void}
   */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const visibleRows = useMemo(() => {
    const filtered = includeSearch
      ? data.filter((row) => rowMatchesSearch(row, searchQuery, columns))
      : data;

    if (!sortField) return filtered;

    const column = columns.find((col) => col.key === sortField);
    if (!column) return filtered;

    return [...filtered].sort((a, b) =>
      compareSortValues(
        resolveSortValue(a, column),
        resolveSortValue(b, column),
        sortDirection
      )
    );
  }, [columns, data, includeSearch, searchQuery, sortDirection, sortField]);

  return (
    <div className={className}>
      {includeSearch ? (
        <div className="mb-2 px-1">
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={searchPlaceholder}
            className={searchClassName}
          />
        </div>
      ) : null}
      <table className={tableClassName}>
        {columns.some((column) => column.width) ? (
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              />
            ))}
          </colgroup>
        ) : null}
        <thead className={theadClassName}>
          <tr className={headerRowClassName}>
            {columns.map((column) => (
              <HeaderCell
                key={column.key}
                column={column}
                isActive={sortField === column.key}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            ))}
          </tr>
        </thead>
        <tbody className={tbodyClassName}>
          {visibleRows.length === 0 ? (
            <tr>
              <td
                colSpan={Math.max(columns.length, 1)}
                className="px-3 py-4 text-center text-sm text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            visibleRows.map((row, index) => {
              const extraRowProps =
                typeof getRowProps === "function" ? getRowProps(row) || {} : {};
              const {
                className: extraRowClassName = "",
                ...restRowProps
              } = extraRowProps;

              return (
                <tr
                  key={getRowKey(row, index)}
                  className={`${rowClassName} ${extraRowClassName}`.trim()}
                  {...restRowProps}
                >
                  {columns.map((column) => (
                    <BodyCell
                      key={column.key}
                      row={row}
                      column={column}
                      cellClassName={cellClassName}
                    />
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SimpleTable;
